/**
 * PDF のテキスト選択範囲から、ページ単位のハイライト領域 (% 座標) を計算するユーティリティ。
 *
 * ページをまたぐ選択でも正しく動作させるため、以下の 2 点を守る:
 *   1. Range.getClientRects() をそのまま使わない
 *      → 複数ページ選択時は「途中の要素の border box」（＝ページ全体の矩形）が
 *        含まれてしまい、ページ全体がハイライトされたように見える。
 *        テキストノード単位で矩形を集めることで実際の行だけを対象にする。
 *   2. pageIndex を選択全体で 1 つに決め打ちしない
 *      → 矩形ごとに、その矩形が属するページ要素を判定して % 座標を算出する。
 */

/** ハイライトエリア（既存データとの互換性を維持する % ベース座標） */
export type HighlightArea = {
  pageIndex: number
  height: number
  width: number
  left: number
  top: number
}

type PageBox = {
  pageIndex: number
  rect: DOMRect
}

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value))

/** 選択範囲に含まれるテキストノードの矩形だけを集める */
const collectTextRects = (range: Range): DOMRect[] => {
  const rootNode = range.commonAncestorContainer
  // 単一テキストノード内の選択はそのまま getClientRects() で問題ない
  if (rootNode.nodeType === Node.TEXT_NODE) {
    return Array.from(range.getClientRects())
  }

  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT)
  const rects: DOMRect[] = []

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const textNode = node as Text
    if (!textNode.data.trim()) continue
    if (!range.intersectsNode(textNode)) continue

    const subRange = document.createRange()
    subRange.selectNodeContents(textNode)
    if (textNode === range.startContainer) subRange.setStart(textNode, range.startOffset)
    if (textNode === range.endContainer) subRange.setEnd(textNode, range.endOffset)
    if (subRange.collapsed) continue

    rects.push(...Array.from(subRange.getClientRects()))
  }

  // テキストノードが 1 つも拾えなかった場合のフォールバック
  return rects.length > 0 ? rects : Array.from(range.getClientRects())
}

/** 選択範囲の周辺にある PDF ページ要素とその矩形を集める */
const collectPageBoxes = (range: Range): PageBox[] => {
  const anchor = range.commonAncestorContainer
  const element = anchor instanceof Element ? anchor : anchor.parentElement
  const scope: ParentNode = element?.closest('.pdf-document-container') ?? document

  return Array.from(scope.querySelectorAll('[data-page-index]'))
    .map((el) => ({
      pageIndex: Number(el.getAttribute('data-page-index')),
      rect: el.getBoundingClientRect(),
    }))
    .filter((box) => Number.isFinite(box.pageIndex) && box.rect.width > 0 && box.rect.height > 0)
}

/** 矩形が属するページを、中心点の内包 → 縦方向の重なり量 の順で判定する */
const findPageBox = (rect: DOMRect, pageBoxes: PageBox[]): PageBox | null => {
  const centerY = rect.top + rect.height / 2
  const centerX = rect.left + rect.width / 2

  const containing = pageBoxes.find(
    ({ rect: p }) => centerY >= p.top && centerY <= p.bottom && centerX >= p.left && centerX <= p.right,
  )
  if (containing) return containing

  let best: PageBox | null = null
  let bestOverlap = 0
  for (const box of pageBoxes) {
    const overlap = Math.min(rect.bottom, box.rect.bottom) - Math.max(rect.top, box.rect.top)
    if (overlap > bestOverlap) {
      bestOverlap = overlap
      best = box
    }
  }
  return best
}

/**
 * 選択範囲からハイライトエリア (% 座標) を計算する。
 * 矩形ごとに所属ページを判定するため、ページをまたぐ選択にも対応する。
 */
export const buildHighlightAreas = (range: Range): HighlightArea[] => {
  const pageBoxes = collectPageBoxes(range)
  if (pageBoxes.length === 0) return []

  const areas: HighlightArea[] = []

  for (const rect of collectTextRects(range)) {
    if (rect.width <= 0 || rect.height <= 0) continue

    const pageBox = findPageBox(rect, pageBoxes)
    // ページ要素が特定できない矩形は、基準が無く座標が破綻するのでスキップ
    if (!pageBox) continue

    const { rect: pageRect } = pageBox
    const left = clampPercent(((rect.left - pageRect.left) / pageRect.width) * 100)
    const top = clampPercent(((rect.top - pageRect.top) / pageRect.height) * 100)
    areas.push({
      pageIndex: pageBox.pageIndex,
      left,
      top,
      width: clampPercent((rect.width / pageRect.width) * 100 + left) - left,
      height: clampPercent((rect.height / pageRect.height) * 100 + top) - top,
    })
  }

  return areas.filter((area) => area.width > 0 && area.height > 0)
}
